import { createShapeId } from 'tldraw';
import { getActivityId, getPinCardSize } from './autoLayout';
import {
  nudgeAwayFromOccupied,
} from './suggestPinPlacement';

/** Minimum clear space between pin cards on every placement change. */
export const PIN_SEPARATION_GAP = 40;
const FRAME_PADDING = 32;
const MAX_PINS_PER_ROW = 3;
const DEFAULT_PIN_W = 300;
const DEFAULT_PIN_H = 400;
const PIN_SHAPE_PREFIX = 'pin:';

function activityById(activities, activityId) {
  if (!activities?.length) return null;
  return activities.find(a => getActivityId(a) === String(activityId)) || null;
}

/**
 * Prefer live editor shape size, then activity-computed size, then placement.
 */
export function resolveActualPinSize(editor, activities, placement) {
  const activityId = String(placement.activityId);
  const shapeId = createShapeId(`${PIN_SHAPE_PREFIX}${activityId}`);
  const shape =
    editor?.getShape?.(shapeId) ||
    editor
      ?.getCurrentPageShapes?.()
      ?.find(s => s.type === 'pin-card' && String(s.meta?.activityId) === activityId);

  if (shape?.props?.w > 0 && shape?.props?.h > 0) {
    return { w: shape.props.w, h: shape.props.h };
  }

  const activity = activityById(activities, activityId);
  if (activity) {
    const size = getPinCardSize(activity, placement.width, {
      manualResize: Boolean(placement.manualSize),
    });
    return { w: size.width, h: size.height };
  }

  return {
    w: Number(placement.width) > 0 ? Number(placement.width) : DEFAULT_PIN_W,
    h: Number(placement.height) > 0 ? Number(placement.height) : DEFAULT_PIN_H,
  };
}

function computeGrid(entries, gap) {
  const cols = Math.min(MAX_PINS_PER_ROW, Math.max(1, entries.length));
  const colWidths = Array.from({ length: cols }, () => 0);
  const rowHeights = [];

  entries.forEach((entry, index) => {
    const col = index % cols;
    const row = Math.floor(index / cols);
    colWidths[col] = Math.max(colWidths[col], entry.w);
    rowHeights[row] = Math.max(rowHeights[row] || 0, entry.h);
  });

  const colStarts = [];
  let x = 0;
  for (let col = 0; col < cols; col += 1) {
    colStarts[col] = x;
    x += colWidths[col] + (col < cols - 1 ? gap : 0);
  }

  const rowStarts = [];
  let y = 0;
  for (let row = 0; row < rowHeights.length; row += 1) {
    rowStarts[row] = y;
    y += rowHeights[row] + (row < rowHeights.length - 1 ? gap : 0);
  }

  const contentWidth = colWidths.reduce(
    (sum, width, index) => sum + width + (index < colWidths.length - 1 ? gap : 0),
    0
  );
  const contentHeight = rowHeights.reduce(
    (sum, height, index) => sum + height + (index < rowHeights.length - 1 ? gap : 0),
    0
  );

  return { cols, colStarts, rowStarts, contentWidth, contentHeight };
}

/**
 * Pack pins into a frame using each pin's real size + gap. Returns placements and
 * a frame large enough that every pin is fully visible.
 */
export function packPinsIntoFrame(frame, members, gap = PIN_SEPARATION_GAP) {
  if (!members.length) return { frame, placements: [] };

  const originX = (Number(frame.x) || 0) + FRAME_PADDING;
  const originY = (Number(frame.y) || 0) + FRAME_PADDING;
  const grid = computeGrid(members, gap);

  const placements = members.map((entry, index) => {
    const col = index % grid.cols;
    const row = Math.floor(index / grid.cols);
    return {
      ...entry.placement,
      activityId: entry.activityId,
      frameId: frame.id,
      x: originX + grid.colStarts[col],
      y: originY + grid.rowStarts[row],
      width: entry.w,
      height: entry.h,
    };
  });

  const nextFrame = {
    ...frame,
    w: Math.max(Number(frame.w) || 0, grid.contentWidth + FRAME_PADDING * 2),
    h: Math.max(Number(frame.h) || 0, grid.contentHeight + FRAME_PADDING * 2),
  };

  return { frame: nextFrame, placements };
}

function rectsOverlapWithGap(a, b, gap) {
  return (
    a.x < b.x + b.w + gap &&
    a.x + a.w + gap > b.x &&
    a.y < b.y + b.h + gap &&
    a.y + a.h + gap > b.y
  );
}

function collectOccupiedExcluding(editor, excludeActivityIds) {
  const occupiedRects = [];
  if (!editor?.getCurrentPageShapes) return occupiedRects;

  for (const shape of editor.getCurrentPageShapes()) {
    if (
      shape.type !== 'pin-card' &&
      shape.type !== 'suggest-pin' &&
      shape.type !== 'conversation-pin'
    ) {
      continue;
    }
    const activityId =
      shape.meta?.activityId != null ? String(shape.meta.activityId) : null;
    if (activityId && excludeActivityIds.has(activityId)) continue;
    occupiedRects.push({
      x: shape.x,
      y: shape.y,
      w: shape.props?.w ?? DEFAULT_PIN_W,
      h: shape.props?.h ?? DEFAULT_PIN_H,
    });
  }
  return occupiedRects;
}

/**
 * Nudge placements so none overlap each other or existing canvas pins, using
 * real sizes and a consistent gap.
 */
export function separateUnframedPlacements(
  editor,
  placements,
  {
    activities,
    gap = PIN_SEPARATION_GAP,
    ignoreActivityIds = new Set(),
    seedOccupied = [],
  } = {}
) {
  const movingIds = new Set([
    ...ignoreActivityIds,
    ...placements.map(p => String(p.activityId)),
  ]);
  const occupiedRects = [
    ...seedOccupied,
    ...collectOccupiedExcluding(editor, movingIds),
  ];

  const resolved = [];
  placements.forEach((placement, index) => {
    const { w, h } = resolveActualPinSize(editor, activities, placement);
    const preferred = {
      x: Number.isFinite(Number(placement.x)) ? Number(placement.x) : 80,
      y: Number.isFinite(Number(placement.y)) ? Number(placement.y) : 80,
    };

    let position = nudgeAwayFromOccupied(preferred, occupiedRects, index, { w, h });
    let guard = 0;
    while (
      guard < 12 &&
      occupiedRects.some(other =>
        rectsOverlapWithGap({ x: position.x, y: position.y, w, h }, other, gap)
      )
    ) {
      position = nudgeAwayFromOccupied(
        { x: position.x + w + gap, y: position.y },
        occupiedRects,
        index + guard,
        { w, h }
      );
      guard += 1;
    }

    const next = {
      ...placement,
      activityId: String(placement.activityId),
      x: position.x,
      y: position.y,
      width: w,
      height: h,
    };
    resolved.push(next);
    occupiedRects.push({ x: next.x, y: next.y, w, h });
  });

  return resolved;
}

/**
 * Every job that changes pin locations should go through this:
 * use real pin sizes, keep a gap, and pack framed pins so they stay visible.
 *
 * @param {object} [options.existingPlacements] — board placements already on canvas;
 *   framed members not in this update batch are included so packing does not
 *   stack a new pin on top of existing frame mates.
 */
export function separatePinPlacements(
  editor,
  placements = [],
  {
    activities,
    frames = [],
    existingPlacements = [],
    gap = PIN_SEPARATION_GAP,
  } = {}
) {
  if (!placements.length) {
    return { placements: [], frames };
  }

  const updateById = new Map(
    placements.map(placement => [String(placement.activityId), placement])
  );

  // Include existing framed mates so we re-pack the whole frame group.
  const framesById = new Map((frames || []).map(f => [String(f.id), { ...f }]));
  const frameIdsTouched = new Set(
    placements
      .filter(p => p.frameId != null && framesById.has(String(p.frameId)))
      .map(p => String(p.frameId))
  );

  const allForSizing = [...placements];
  for (const existing of existingPlacements || []) {
    const id = String(existing.activityId);
    if (updateById.has(id)) continue;
    if (existing.frameId != null && frameIdsTouched.has(String(existing.frameId))) {
      allForSizing.push(existing);
    }
  }

  const sized = allForSizing.map(placement => {
    const activityId = String(placement.activityId);
    const preferred = updateById.get(activityId) || placement;
    const { w, h } = resolveActualPinSize(editor, activities, preferred);
    return {
      activityId,
      w,
      h,
      placement: {
        ...preferred,
        activityId,
        width: w,
        height: h,
        frameId: preferred.frameId ?? placement.frameId ?? null,
      },
    };
  });

  const framed = sized.filter(
    entry => entry.placement.frameId != null && framesById.has(String(entry.placement.frameId))
  );
  const unframed = sized.filter(
    entry =>
      updateById.has(entry.activityId) &&
      (entry.placement.frameId == null || !framesById.has(String(entry.placement.frameId)))
  );

  const nextFrames = [];
  const packedPlacements = [];
  const packedRects = [];
  const framedIds = new Set();

  const membersByFrame = new Map();
  for (const entry of framed) {
    const frameId = String(entry.placement.frameId);
    if (!membersByFrame.has(frameId)) membersByFrame.set(frameId, []);
    membersByFrame.get(frameId).push(entry);
    framedIds.add(entry.activityId);
  }

  for (const frame of frames || []) {
    const frameId = String(frame.id);
    const members = membersByFrame.get(frameId) || [];
    if (!members.length) {
      nextFrames.push(frame);
      continue;
    }
    const { frame: fitted, placements: packed } = packPinsIntoFrame(
      frame,
      members,
      gap
    );
    nextFrames.push(fitted);
    packedPlacements.push(...packed);
    for (const p of packed) {
      packedRects.push({ x: p.x, y: p.y, w: p.width, h: p.height });
    }
  }

  const separatedUnframed = separateUnframedPlacements(
    editor,
    unframed.map(entry => entry.placement),
    {
      activities,
      gap,
      ignoreActivityIds: framedIds,
      seedOccupied: packedRects,
    }
  );

  return {
    placements: [...packedPlacements, ...separatedUnframed],
    frames: nextFrames.length ? nextFrames : frames,
  };
}
